/**
 * The hero figure's data and the two geometries it is drawn in.
 *
 * Five contacts, each a green dot in a crowd and a card that says why they are one. The names,
 * quotes and tags are illustrative and say so in the figure's description — what is NOT invented is
 * the shape of the claim: someone published a need, on a date, and it scored.
 */
export type HeroPick = {
  key: 'marketing' | 'crm' | 'analytics' | 'billing' | 'hiring'
  /** Out of 100. Drives both the printed number and the bar's length. */
  score: number
  days: number
}

export const PICKS: readonly HeroPick[] = [
  /* Four of the five are people asking for SOFTWARE, because that is who this product is sold to:
     a buyer looking at this figure should see their own category being asked for, not a hiring board.
     The fifth stays a people request — the network carries both, and pretending otherwise would be
     its own kind of lie. */
  { key: 'marketing', score: 84, days: 2 },
  { key: 'crm', score: 79, days: 4 },
  { key: 'analytics', score: 74, days: 6 },
  { key: 'billing', score: 81, days: 3 },
  { key: 'hiring', score: 71, days: 9 },
]

/** The score counts up through these on the way to the first card's 84 — the reveal, not data. */
export const COUNT_UP = [0, 24, 47, 66, 78] as const

export type HeroLayout = {
  viewBox: string
  /** The anonymous crowd: the scale the five are picked out of. */
  crowd: readonly (readonly [number, number])[]
  /** Where the five green dots sit, in pick order. */
  seats: readonly (readonly [number, number])[]
  dotRadius: number
  /** The picked dot is drawn slightly larger, and larger again while it is the one on show. */
  pickRadius: number
  pickedRadius: number
  hitRadius: number
  /** The connector runs from under each seat down to the card's top edge. */
  connectorY: number
  card: { x: number; y: number; width: number; height: number }
  eyebrowY: number
  /** One line wide, two lines narrow — a 15px quote does not fit 324px in one. */
  quoteLines: readonly number[]
  tag: { x: number; y: number; height: number }
  metaY: number
  /** The bar the score fills, and the printed number's baseline. */
  bar: { x1: number; x2: number; y: number }
  scoreAnchor: { x: number; y: number; labelY: number; textAnchor: 'start' | 'end' }
  /** Per-character estimate for the tag's pill, because the label is translated. */
  tagCharWidth: number
  tagPadding: number
}

const grid = (columns: readonly number[], rows: readonly (readonly [number, readonly number[]])[]) =>
  rows.flatMap(([y, indices]) => indices.map((index) => [columns[index] as number, y] as const))

/**
 * Wide: a 5-column crowd four rows deep, the five picks sitting in the gaps of the last two rows so
 * they read as part of the same lattice rather than as a separate row of five.
 */
const WIDE_COLUMNS = [45, 135, 225, 315, 405]
const WIDE_OFFSET = [75, 165, 255, 345, 435]

export const WIDE: HeroLayout = {
  viewBox: '0 0 460 397',
  crowd: [
    ...grid(WIDE_COLUMNS, [[20, [0, 1, 2, 3, 4]]]),
    ...grid(WIDE_OFFSET, [[58, [0, 1, 2, 3, 4]]]),
    ...grid(WIDE_COLUMNS, [[96, [0, 1, 2, 3, 4]]]),
    ...grid(WIDE_OFFSET, [[134, [1, 3]]]),
    ...grid(WIDE_COLUMNS, [[172, [0, 2, 4]]]),
  ],
  seats: [
    [75, 134],
    [135, 172],
    [255, 134],
    [315, 172],
    [435, 134],
  ],
  dotRadius: 19.5,
  pickRadius: 21.6,
  pickedRadius: 24,
  hitRadius: 30,
  connectorY: 216,
  card: { x: 20, y: 216, width: 420, height: 170 },
  eyebrowY: 246,
  quoteLines: [284],
  tag: { x: 40, y: 329, height: 20 },
  metaY: 344,
  bar: { x1: 286, x2: 420, y: 374 },
  scoreAnchor: { x: 420, y: 344, labelY: 362, textAnchor: 'end' },
  tagCharWidth: 6.9,
  tagPadding: 24,
}

/** Narrow: the same lattice at a tighter pitch, and the card grows to hold a two-line quote. */
const NARROW_COLUMNS = [48, 110, 172, 234, 296]
const NARROW_OFFSET = [70, 132, 194, 256, 318]

export const NARROW: HeroLayout = {
  viewBox: '0 0 340 430',
  crowd: [
    ...grid(NARROW_COLUMNS, [[18, [0, 1, 2, 3, 4]]]),
    ...grid(NARROW_OFFSET, [[54, [0, 1, 2, 3, 4]]]),
    ...grid(NARROW_COLUMNS, [[90, [0, 1, 2, 3, 4]]]),
    ...grid(NARROW_OFFSET, [[126, [1, 3]]]),
    ...grid(NARROW_COLUMNS, [[162, [0, 2, 4]]]),
  ],
  seats: [
    [70, 126],
    [110, 162],
    [194, 126],
    [234, 162],
    [318, 126],
  ],
  dotRadius: 19.5,
  pickRadius: 21.6,
  pickedRadius: 24,
  hitRadius: 30,
  connectorY: 230,
  card: { x: 8, y: 230, width: 324, height: 190 },
  eyebrowY: 260,
  quoteLines: [296, 320],
  tag: { x: 26, y: 336, height: 22 },
  metaY: 351,
  bar: { x1: 26, x2: 314, y: 410 },
  scoreAnchor: { x: 96, y: 394, labelY: 394, textAnchor: 'end' },
  tagCharWidth: 8.6,
  tagPadding: 26,
}

/** Where the bar reaches at a given score — the same arithmetic in both layouts. */
export const barEnd = (layout: HeroLayout, score: number) =>
  layout.bar.x1 + ((layout.bar.x2 - layout.bar.x1) * score) / 100
